/**
 * FleetDashboardPage — `/fleet` index landing.
 *
 * Vehicles live in service-delivery (`/v1/vehicles`) since they're operated
 * by drivers; service-franchise has FranchiseAsset / Inventory but doesn't
 * model "vehicle" specifically. This dashboard reads counts via
 * `GET /v1/vehicles` (operator-scoped) and groups by status.
 */
import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Truck, Wrench } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import { listVehicles, type VehicleStatus } from '../../lib/api/delivery';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

export function FleetDashboardPage(): JSX.Element {
  const all = useQuery({
    queryKey: ['fleet', 'vehicles', 'all'],
    queryFn: () => listVehicles({ pageSize: 100 }),
    retry: false,
    staleTime: 30_000,
  });

  const denied =
    all.isError && all.error instanceof ApiError && (all.error.status === 401 || all.error.status === 403);

  const counts: Record<VehicleStatus, number> = { ACTIVE: 0, MAINTENANCE: 0, RETIRED: 0 };
  for (const v of all.data?.items ?? []) {
    counts[v.status] += 1;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Fleet"
        description="Operator-scoped vehicle registry."
        actions={
          <Link
            to="/fleet/register"
            className="inline-flex items-center gap-2 px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark"
          >
            <Plus className="w-4 h-4" /> Register vehicle
          </Link>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Total vehicles"
          value={all.isPending ? '—' : denied ? '—' : String(all.data?.total ?? 0)}
          hint={denied ? 'Permission required' : 'Vehicles in operator fleet'}
          icon={Truck}
          tone={denied ? 'warning' : 'default'}
        />
        <KPICard title="Active" value={String(counts.ACTIVE)} hint="status=ACTIVE" tone="success" />
        <KPICard title="In maintenance" value={String(counts.MAINTENANCE)} hint="status=MAINTENANCE" icon={Wrench} tone="warning" />
        <KPICard title="Retired" value={String(counts.RETIRED)} hint="status=RETIRED" />
      </div>

      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Vehicles</h2>
        {all.isPending ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : denied ? (
          <DeferredNotice title="Permission required">
            The dev token doesn&apos;t carry <code className="font-mono">Delivery/readVehicle</code>.
          </DeferredNotice>
        ) : all.isError ? (
          <DeferredNotice title="Service unreachable">{(all.error as Error).message}</DeferredNotice>
        ) : (
          <div className="bg-white border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-4 py-2">Registration</th>
                  <th className="text-left px-4 py-2">Make / Model</th>
                  <th className="text-left px-4 py-2">Kind</th>
                  <th className="text-left px-4 py-2">Capacity</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-right px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(all.data?.items ?? []).map((v) => (
                  <tr key={v.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs font-bold text-gray-900">{v.registration}</td>
                    <td className="px-4 py-2 text-xs text-gray-700">{v.make} {v.model}</td>
                    <td className="px-4 py-2 text-xs text-muted">{v.kind}</td>
                    <td className="px-4 py-2 text-xs text-muted">{v.capacityKg} kg</td>
                    <td className="px-4 py-2"><StatusBadge status={v.status} /></td>
                    <td className="px-4 py-2 text-right">
                      <Link to={`/fleet/vehicles/${encodeURIComponent(v.id)}`} className="text-xs font-semibold text-nipost-green hover:underline">
                        Open →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(all.data?.items.length ?? 0) === 0 ? (
              <p className="p-8 text-center text-sm text-muted">No vehicles registered yet.</p>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
